import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { trpc } from '../lib/trpc'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * **Anmeldung über NetCon.Cloud an der Umgebung** (05.11.2026, E-464; NetCon.Cloud ADR 0012).
 *
 * Nils: *„Ein Entwickler soll dann einfach das erstellen können oder verknüpfen wenn es schon
 * existiert, alle relevanten Daten zum Kopieren bekommen mit Anleitung wo was hin muss."*
 *
 * Drei Zustände: nichts verknüpft (verknüpfen oder anlegen), gerade angelegt bzw. Secret erneuert
 * (das Secret steht **dieses eine Mal** da), verknüpft (Werte, Anleitung, Personen).
 */

type Reiter = 'einbinden' | 'personen'
type Ausgabe = Awaited<ReturnType<typeof trpc.umgebungCloud.anlegen.mutate>>
type Coolify = { gesetzt: string[]; fehler: string | null } | null

const KNOPF =
  'cursor-pointer rounded-lg border border-line2 bg-surface px-2.5 py-1 font-semibold text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-50'
const KNOPF_HAUPT =
  'cursor-pointer rounded-lg bg-accent px-3 py-1.5 font-semibold text-white hover:brightness-105 disabled:cursor-default disabled:opacity-50'
const FELD = 'w-full rounded-lg border border-line2 bg-surface px-2.5 py-1.5 text-[12.5px]'

/** Was in die Umgebung des Servers gehört – Erklärung je Variable. */
const ZWECK: Record<string, string> = {
  NETCON_ISSUER: 'Aussteller – daraus liest die App das Discovery-Dokument',
  NETCON_CLIENT_ID: 'Client der Anmeldung (öffentlich, darf auch der Browser kennen)',
  NETCON_CLIENT_SECRET: 'Secret der Anmeldung – nur Server',
  NETCON_SERVER_CLIENT_ID: 'Dienstkonto des Servers (Benutzer, Rückweg, NetCon-Token)',
  NETCON_SERVER_CLIENT_SECRET: 'Secret des Dienstkontos – nur Server, nie ins Browser-Bundle',
  NETCON_CLOUD_PORTAL: 'Portal für /api/v1/…',
}
const istSecret = (name: string) => name.endsWith('_SECRET')

function useKopieren() {
  const [kopiert, setKopiert] = useState<string | null>(null)
  const kopieren = async (was: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setKopiert(was)
      setTimeout(() => setKopiert((k) => (k === was ? null : k)), 1800)
    } catch {
      window.prompt('Kopieren mit ⌘C:', text)
    }
  }
  return { kopiert, kopieren }
}

export function CloudAppFenster({
  umgebungId,
  label,
  darfAendern,
  schliessen,
}: {
  umgebungId: string
  label: string
  darfAendern: boolean
  schliessen: () => void
}) {
  const queryClient = useQueryClient()
  const [reiter, setReiter] = useState<Reiter>('einbinden')
  /** Ergebnis von Anlegen oder Erneuern – das einzige Mal, dass ein Secret im Browser ist. */
  const [frisch, setFrisch] = useState<{
    secret: string | null
    werte: Record<string, string>
    coolify: Coolify
    freigeschaltet?: string | null
  } | null>(null)

  const stand = useQuery({
    queryKey: ['umgebungCloud', 'stand', umgebungId],
    queryFn: () => trpc.umgebungCloud.stand.query({ umgebungId }),
    retry: false,
  })
  const s = stand.data
  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['umgebungCloud'] })
    void queryClient.invalidateQueries({ queryKey: ['umgebung'] })
  }

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      schliessen()
    }
    window.addEventListener('keydown', taste, true)
    return () => window.removeEventListener('keydown', taste, true)
  }, [schliessen])

  const { kopiert, kopieren } = useKopieren()
  const app = s?.app?.anwendung ?? null

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-[60] flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[5vh]"
      {...hintergrundSchliesst(schliessen, frisch === null)}
      role="dialog"
      aria-modal="true"
      aria-label={`NetCon.Cloud – ${label}`}
    >
      <div className="flex h-[min(88vh,100%)] w-full max-w-[860px] min-w-0 flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line px-[18px] py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-bold">Anmeldung über NetCon.Cloud · {label}</h3>
            <p className="text-[11.5px] text-muted">
              {!s
                ? 'NetCon.Cloud wird gefragt …'
                : s.verknuepft
                  ? `${s.verknuepft.name} · ${app?.clientId ?? s.verknuepft.key}${app ? ` · ${app.art === 'public' ? 'Browser-App mit PKCE' : 'serverseitig mit Secret'}` : ''}`
                  : 'Noch keine App verknüpft'}
            </p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2 text-[12px]">
            {s?.app && (
              <button
                type="button"
                className={KNOPF}
                title="Die Inbetriebnahme aus NetCon.Cloud mit den echten Werten – für die Entwicklungs-KI"
                onClick={() => void kopieren('anleitung', s.app?.anleitung ?? '')}
              >
                {kopiert === 'anleitung' ? '✓ kopiert' : 'Anleitung für Claude kopieren'}
              </button>
            )}
            {app?.verwaltungUrl && (
              <a
                href={app.verwaltungUrl}
                target="_blank"
                rel="noreferrer"
                className="text-accent-deep hover:underline"
              >
                In NetCon.Cloud ↗
              </a>
            )}
            <button
              type="button"
              onClick={schliessen}
              className="cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>
        </div>

        {s?.verknuepft && (
          <div className="flex gap-1 border-b border-line px-[14px] text-[12.5px]" role="tablist">
            {(
              [
                ['einbinden', 'Einbinden'],
                ['personen', 'Personen'],
              ] as const
            ).map(([id, text]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={reiter === id}
                onClick={() => setReiter(id)}
                className={`-mb-px cursor-pointer border-b-2 px-2.5 py-2 ${
                  reiter === id
                    ? 'border-accent font-semibold text-ink'
                    : 'border-transparent text-muted hover:text-ink2'
                }`}
              >
                {text}
              </button>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-4 text-[12.5px]">
          {stand.error && <p className="text-bad">{stand.error.message}</p>}
          {s?.fehler && <p className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-bad">{s.fehler}</p>}
          {s && !s.verknuepft && (
            <Verbinden
              umgebungId={umgebungId}
              stand={s}
              darfAendern={darfAendern}
              fertig={(a) => {
                if (a)
                  setFrisch({
                    secret: a.secret,
                    werte: a.werte,
                    coolify: a.coolify,
                    freigeschaltet: a.freigeschaltet,
                  })
                neuLaden()
              }}
            />
          )}
          {s?.verknuepft && reiter === 'einbinden' && (
            <Einbinden
              umgebungId={umgebungId}
              stand={s}
              frisch={frisch}
              setFrisch={setFrisch}
              darfAendern={darfAendern}
              neuLaden={neuLaden}
            />
          )}
          {s?.verknuepft && reiter === 'personen' && (
            <Personen
              umgebungId={umgebungId}
              appName={s.verknuepft.name}
              darfAendern={darfAendern}
            />
          )}
        </div>
      </div>
    </div>
  )
}

type Stand = Awaited<ReturnType<typeof trpc.umgebungCloud.stand.query>>

/* ── Nichts verknüpft: vorhandene wählen oder neu anlegen ─────────────── */

function Verbinden({
  umgebungId,
  stand,
  darfAendern,
  fertig,
}: {
  umgebungId: string
  stand: Stand
  darfAendern: boolean
  fertig: (angelegt: Extract<Ausgabe, { ok: true }> | null) => void
}) {
  const [modus, setModus] = useState<'neu' | 'vorhanden'>(stand.vorschlag ? 'vorhanden' : 'neu')
  const [key, setKey] = useState(stand.vorschlag?.key ?? '')
  const [name, setName] = useState(stand.neu.name)
  const [url, setUrl] = useState(stand.neu.url)
  const [art, setArt] = useState<'public' | 'confidential'>(stand.neu.art)
  const [schluessel, setSchluessel] = useState('')
  const [inCoolify, setInCoolify] = useState(stand.mitCoolify)
  const [michFreischalten, setMichFreischalten] = useState(true)
  const [vorhanden, setVorhanden] = useState<{ key: string; name: string; meldung: string } | null>(
    null,
  )

  const apps = useQuery({
    queryKey: ['umgebungCloud', 'apps'],
    queryFn: () => trpc.umgebungCloud.apps.query(),
    enabled: darfAendern && modus === 'vorhanden',
    retry: false,
  })
  const verknuepfen = useMutation({
    mutationFn: (k: string) => trpc.umgebungCloud.verknuepfen.mutate({ umgebungId, key: k }),
    onSuccess: () => fertig(null),
  })
  const anlegen = useMutation({
    mutationFn: () =>
      trpc.umgebungCloud.anlegen.mutate({
        umgebungId,
        name,
        url,
        art,
        ...(schluessel.trim() ? { key: schluessel.trim() } : {}),
        inCoolify,
        michFreischalten,
      }),
    onSuccess: (a) => {
      if (a.ok) fertig(a)
      else setVorhanden({ key: a.vorhanden.key, name: a.vorhanden.name, meldung: a.meldung })
    },
  })

  if (!darfAendern) {
    return (
      <p className="text-muted">
        An dieser Umgebung hängt noch keine App aus NetCon.Cloud. Anlegen und verknüpfen kann das
        Team.
      </p>
    )
  }

  return (
    <div className="grid gap-4">
      <p className="text-ink2">
        Eine App in NetCon.Cloud ist die Anmeldung dieser Umgebung: Keycloak-Client, Kachel im
        Umschalter, Freischaltung von Personen. Rechte in der App bleiben in der App.
      </p>

      {stand.vorschlag && (
        <div className="rounded-xl border border-good-text/30 bg-good-bg px-3 py-2.5">
          <b>{stand.vorschlag.name}</b> ist in NetCon.Cloud schon unter{' '}
          <span className="font-mono">{stand.vorschlag.url}</span> registriert.{' '}
          <button
            type="button"
            className="cursor-pointer font-semibold text-accent-deep hover:underline"
            disabled={verknuepfen.isPending}
            onClick={() => verknuepfen.mutate(stand.vorschlag?.key ?? '')}
          >
            Mit dieser Umgebung verknüpfen
          </button>
        </div>
      )}

      <div className="flex gap-4">
        {(
          [
            ['neu', 'Neu anlegen'],
            ['vorhanden', 'Vorhandene verknüpfen'],
          ] as const
        ).map(([id, text]) => (
          <label key={id} className="flex cursor-pointer items-center gap-1.5 font-semibold">
            <input type="radio" checked={modus === id} onChange={() => setModus(id)} />
            {text}
          </label>
        ))}
      </div>

      {modus === 'vorhanden' && (
        <div className="grid gap-2">
          {apps.error && <p className="text-bad">{apps.error.message}</p>}
          <select className={FELD} value={key} onChange={(e) => setKey(e.target.value)}>
            <option value="">{apps.isLoading ? 'wird geladen …' : '– App wählen –'}</option>
            {apps.data?.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name} · {a.url.replace(/^https:\/\//, '')} · {a.clientId ?? 'ohne Client'}
              </option>
            ))}
          </select>
          <div>
            <button
              type="button"
              className={KNOPF_HAUPT}
              disabled={!key || verknuepfen.isPending}
              onClick={() => verknuepfen.mutate(key)}
            >
              {verknuepfen.isPending ? 'wird verknüpft …' : 'Verknüpfen'}
            </button>
          </div>
          {verknuepfen.error && <p className="text-bad">{verknuepfen.error.message}</p>}
        </div>
      )}

      {modus === 'neu' && (
        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1">
              <span className="text-[11.5px] text-muted">Name (Kopfleiste, Umschalter)</span>
              <input className={FELD} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="text-[11.5px] text-muted">Schlüssel (leer = aus dem Namen)</span>
              <input
                className={`${FELD} font-mono`}
                value={schluessel}
                placeholder="z. B. evidence"
                onChange={(e) => setSchluessel(e.target.value)}
              />
            </label>
          </div>
          <label className="grid gap-1">
            <span className="text-[11.5px] text-muted">
              Adresse – unter *.netcon.cloud, sonst bleibt die Kopfleiste leer
            </span>
            <input
              className={`${FELD} font-mono`}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <div className="grid gap-1.5">
            <span className="text-[11.5px] text-muted">Wie ist die App gebaut?</span>
            <label className="flex cursor-pointer items-start gap-1.5">
              <input
                type="radio"
                className="mt-0.5"
                checked={art === 'public'}
                onChange={() => setArt('public')}
              />
              <span>
                <b>Browser-App (SPA)</b> – öffentlicher Client mit PKCE; der Server bekommt einen
                eigenen Server-Client mit Secret
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-1.5">
              <input
                type="radio"
                className="mt-0.5"
                checked={art === 'confidential'}
                onChange={() => setArt('confidential')}
              />
              <span>
                <b>Serverseitig</b> (Laravel, Next.js, .NET …) – vertraulicher Client mit Secret
              </span>
            </label>
          </div>
          <div className="grid gap-1">
            <label className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={inCoolify}
                disabled={!stand.mitCoolify}
                onChange={(e) => setInCoolify(e.target.checked)}
              />
              Werte samt Secret gleich in die Coolify-Variablen eintragen
              {!stand.mitCoolify && <span className="text-muted">(keine Coolify-Anwendung)</span>}
            </label>
            <label className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={michFreischalten}
                onChange={(e) => setMichFreischalten(e.target.checked)}
              />
              Mich gleich für die App freischalten
            </label>
          </div>
          {vorhanden && (
            <div className="rounded-xl border border-line2 bg-[#fdf1ea] px-3 py-2.5">
              {vorhanden.meldung}{' '}
              <button
                type="button"
                className="cursor-pointer font-semibold text-accent-deep hover:underline"
                onClick={() => verknuepfen.mutate(vorhanden.key)}
              >
                {vorhanden.name} verknüpfen
              </button>
            </div>
          )}
          <div>
            <button
              type="button"
              className={KNOPF_HAUPT}
              disabled={anlegen.isPending || name.trim().length < 2 || !url.startsWith('https://')}
              onClick={() => {
                setVorhanden(null)
                anlegen.mutate()
              }}
            >
              {anlegen.isPending ? 'wird in NetCon.Cloud angelegt …' : 'In NetCon.Cloud anlegen'}
            </button>
          </div>
          {anlegen.error && <p className="text-bad">{anlegen.error.message}</p>}
        </div>
      )}
    </div>
  )
}

/* ── Verknüpft: Werte, wo sie hingehören, Anleitung ──────────────────── */

function Einbinden({
  umgebungId,
  stand,
  frisch,
  setFrisch,
  darfAendern,
  neuLaden,
}: {
  umgebungId: string
  stand: Stand
  frisch: {
    secret: string | null
    werte: Record<string, string>
    coolify: Coolify
    freigeschaltet?: string | null
  } | null
  setFrisch: (
    f: { secret: string | null; werte: Record<string, string>; coolify: Coolify } | null,
  ) => void
  darfAendern: boolean
  neuLaden: () => void
}) {
  const { kopiert, kopieren } = useKopieren()
  const [erneuernFrage, setErneuernFrage] = useState(false)
  const [loesenFrage, setLoesenFrage] = useState(false)
  const [anleitungOffen, setAnleitungOffen] = useState(false)
  const [coolify, setCoolify] = useState<Coolify>(frisch?.coolify ?? null)
  const app = stand.app?.anwendung
  const werte = frisch?.werte ?? stand.app?.werte ?? {}
  const env = Object.entries(werte)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n')

  const erneuern = useMutation({
    mutationFn: () =>
      trpc.umgebungCloud.secretErneuern.mutate({ umgebungId, inCoolify: stand.mitCoolify }),
    onSuccess: (r) => {
      setErneuernFrage(false)
      setFrisch({ secret: r.secret, werte: r.werte, coolify: r.coolify })
      setCoolify(r.coolify)
    },
  })
  const ohneSecret = useMutation({
    mutationFn: () => trpc.umgebungCloud.werteInCoolify.mutate({ umgebungId }),
    onSuccess: (r) => setCoolify(r),
  })
  const ausrollen = useMutation({
    mutationFn: () => trpc.umgebung.aktion.mutate({ umgebungId, aktion: 'ausrollen' }),
  })
  const loesen = useMutation({
    mutationFn: () => trpc.umgebungCloud.loesen.mutate({ umgebungId }),
    onSuccess: neuLaden,
  })

  if (!app) return null
  const spa = app.art === 'public'

  return (
    <div className="grid gap-4">
      {frisch?.secret && (
        <div className="rounded-xl border-2 border-accent bg-[#fdf1ea] px-3 py-2.5">
          <p className="mb-1 font-bold">Das Secret steht nur jetzt hier.</p>
          <p className="mb-2 text-ink2">
            NetCon.Cloud hebt es nicht auf, Work auch nicht. Es steht unten in den Werten – kopieren
            oder in Coolify eintragen, bevor du das Fenster schließt.
          </p>
          {frisch.freigeschaltet && (
            <p className="text-ink2">
              Du bist für {app.name} freigeschaltet ({frisch.freigeschaltet}).
            </p>
          )}
        </div>
      )}

      {coolify && (
        <div
          className={`rounded-xl px-3 py-2.5 ${coolify.fehler ? 'bg-bad-bg text-bad' : 'bg-good-bg text-good-text'}`}
        >
          {coolify.gesetzt.length > 0 && (
            <>In Coolify eingetragen: {coolify.gesetzt.join(', ')}. </>
          )}
          {coolify.fehler ?? 'Die Anwendung liest sie erst nach dem nächsten Ausrollen.'}{' '}
          {!coolify.fehler && darfAendern && (
            <button
              type="button"
              className="cursor-pointer font-semibold underline"
              disabled={ausrollen.isPending || ausrollen.isSuccess}
              onClick={() => ausrollen.mutate()}
            >
              {ausrollen.isSuccess
                ? '✓ Ausrollen gestartet'
                : ausrollen.isPending
                  ? 'wird gestartet …'
                  : 'Jetzt neu ausrollen'}
            </button>
          )}
          {ausrollen.error && <span className="block">{ausrollen.error.message}</span>}
        </div>
      )}

      <section>
        <h4 className="mb-1 text-[13px] font-bold">1 · Umgebungsvariablen am Server</h4>
        <p className="mb-2 text-ink2">
          {stand.mitCoolify
            ? 'In Coolify unter Environment Variables dieser Anwendung (geht unten mit einem Klick), lokal in die .env.'
            : 'Dort, wo der Server seine Konfiguration liest (Coolify: Environment Variables, lokal .env).'}{' '}
          {spa
            ? 'Der Browser braucht nur NETCON_ISSUER und NETCON_CLIENT_ID; das Secret nie ins Bundle.'
            : 'Alles bleibt auf dem Server.'}
        </p>
        <div className="overflow-hidden rounded-xl border border-line">
          {Object.entries(werte).map(([name, wert]) => (
            <div
              key={name}
              className="flex items-center gap-2 border-t border-line px-3 py-1.5 first:border-t-0"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[12px] font-semibold">{name}</div>
                <div className="text-[11px] text-muted">{ZWECK[name] ?? ''}</div>
              </div>
              <div
                className="max-w-[45%] min-w-0 truncate font-mono text-[12px]"
                title={istSecret(name) ? undefined : wert}
              >
                {wert === '' ? (
                  <span className="text-muted">nur beim Anlegen/Erneuern sichtbar</span>
                ) : istSecret(name) ? (
                  <span className="rounded bg-[#f3f2ef] px-1">{wert}</span>
                ) : (
                  wert
                )}
              </div>
              <button
                type="button"
                className="cursor-pointer text-[11.5px] text-accent-deep hover:underline disabled:invisible"
                disabled={wert === ''}
                onClick={() => void kopieren(name, wert)}
              >
                {kopiert === name ? '✓' : 'kopieren'}
              </button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-[12px]">
          <button type="button" className={KNOPF} onClick={() => void kopieren('env', env)}>
            {kopiert === 'env' ? '✓ kopiert' : 'Alle als .env kopieren'}
          </button>
          {darfAendern && stand.mitCoolify && !frisch?.secret && (
            <button
              type="button"
              className={KNOPF}
              disabled={ohneSecret.isPending}
              title="Issuer, Client-IDs und Portal – das Secret bleibt, wie es in Coolify steht"
              onClick={() => ohneSecret.mutate()}
            >
              {ohneSecret.isPending
                ? 'wird eingetragen …'
                : 'Werte ohne Secret in Coolify eintragen'}
            </button>
          )}
          {darfAendern &&
            (erneuernFrage ? (
              <span className="flex flex-wrap items-center gap-2 rounded-lg bg-[#fdf1ea] px-2 py-1">
                Das alte Secret gilt sofort nicht mehr
                {stand.mitCoolify
                  ? '; das neue kommt gleich in Coolify, danach neu ausrollen.'
                  : '.'}
                <button
                  type="button"
                  className="cursor-pointer font-semibold text-bad hover:underline"
                  disabled={erneuern.isPending}
                  onClick={() => erneuern.mutate()}
                >
                  {erneuern.isPending ? 'wird erneuert …' : 'Ja, erneuern'}
                </button>
                <button
                  type="button"
                  className="cursor-pointer text-muted hover:underline"
                  onClick={() => setErneuernFrage(false)}
                >
                  abbrechen
                </button>
              </span>
            ) : (
              <button type="button" className={KNOPF} onClick={() => setErneuernFrage(true)}>
                Secret erneuern …
              </button>
            ))}
        </div>
        {(erneuern.error ?? ohneSecret.error) && (
          <p className="mt-1 text-bad">{(erneuern.error ?? ohneSecret.error)?.message}</p>
        )}
      </section>

      <section>
        <h4 className="mb-1 text-[13px] font-bold">2 · Im Code</h4>
        <ul className="list-disc space-y-0.5 pl-5 text-ink2">
          <li>
            Rückweg der Anmeldung unter{' '}
            <span className="font-mono">{app.url.replace(/\/+$/, '')}/…</span> – erlaubt ist{' '}
            <span className="font-mono">{app.url.replace(/\/+$/, '')}/*</span>, z. B.{' '}
            <span className="font-mono">/auth/netcon/callback</span>.
          </li>
          <li>
            Abmelden überall: Keycloak ruft{' '}
            <span className="font-mono">/auth/netcon/backchannel-logout</span> – ist beim Client
            schon eingetragen.
          </li>
          <li>
            Kopfleiste: <span className="font-mono">&lt;netcon-header&gt;</span> aus{' '}
            <span className="font-mono">netcon.cloud/embed/header.v1.js</span>.
          </li>
          <li>
            Den ganzen Ablauf (
            {spa
              ? 'SPA mit PKCE, Token einmal gegen eine Sitzung tauschen'
              : 'Authorization Code mit Secret'}
            ), Personen aus NetCon.Cloud und das Design stehen mit den echten Werten in der
            Anleitung. Am schnellsten: <b>„Anleitung für Claude kopieren“</b> oben und in die
            Entwicklungs-Session geben.
          </li>
        </ul>
        <button
          type="button"
          className="mt-2 cursor-pointer text-[12px] text-accent-deep hover:underline"
          onClick={() => setAnleitungOffen((o) => !o)}
        >
          {anleitungOffen ? 'Anleitung ausblenden' : 'Anleitung hier lesen'}
        </button>
        {anleitungOffen && (
          <pre className="mt-2 max-h-[40vh] overflow-auto rounded-xl border border-line bg-[#f7f7f5] p-3 text-[11.5px] whitespace-pre-wrap">
            {stand.app?.anleitung}
          </pre>
        )}
      </section>

      <section>
        <h4 className="mb-1 text-[13px] font-bold">3 · Zugang</h4>
        <p className="text-ink2">
          Sehen und öffnen kann die App nur, wer in NetCon.Cloud freigeschaltet ist – Reiter{' '}
          <b>Personen</b>.
          {app.nurNetconCloud
            ? ' Die App hat keine eigene Anmeldung; Personen kommen nur aus NetCon.Cloud.'
            : ' Die bestehende Anmeldung der App bleibt daneben.'}
        </p>
      </section>

      {darfAendern && (
        <div className="border-t border-line pt-3 text-[12px]">
          {loesenFrage ? (
            <span className="flex items-center gap-2">
              <span className="text-muted">
                Nur die Verknüpfung in Work lösen? Die App in NetCon.Cloud bleibt.
              </span>
              <button
                type="button"
                className="cursor-pointer font-semibold text-bad hover:underline"
                onClick={() => loesen.mutate()}
              >
                lösen
              </button>
              <button
                type="button"
                className="cursor-pointer text-muted hover:underline"
                onClick={() => setLoesenFrage(false)}
              >
                abbrechen
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="cursor-pointer text-muted hover:underline"
              onClick={() => setLoesenFrage(true)}
            >
              Verknüpfung lösen
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Personen: wer die App sieht, einladen, entziehen ───────────────── */

const EINLADUNG: Record<string, string> = {
  netcon: 'angelegt – meldet sich mit dem NetCon-Passwort an, keine Mail',
  verschickt: 'eingeladen – Keycloak schickt eine Mail zum Passwortsetzen',
  fehlgeschlagen: 'angelegt, aber die Einladungsmail ging nicht raus (in NetCon.Cloud nachholen)',
}

function Personen({
  umgebungId,
  appName,
  darfAendern,
}: {
  umgebungId: string
  appName: string
  darfAendern: boolean
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [vorname, setVorname] = useState('')
  const [nachname, setNachname] = useState('')
  const [meldung, setMeldung] = useState<string | null>(null)
  const [entziehenFrage, setEntziehenFrage] = useState<string | null>(null)

  const liste = useQuery({
    queryKey: ['umgebungCloud', 'benutzer', umgebungId],
    queryFn: () => trpc.umgebungCloud.benutzer.query({ umgebungId }),
    retry: false,
  })
  const neu = () =>
    void queryClient.invalidateQueries({ queryKey: ['umgebungCloud', 'benutzer', umgebungId] })
  const einladen = useMutation({
    mutationFn: () =>
      trpc.umgebungCloud.einladen.mutate({
        umgebungId,
        email,
        ...(vorname.trim() ? { vorname: vorname.trim() } : {}),
        ...(nachname.trim() ? { nachname: nachname.trim() } : {}),
      }),
    onSuccess: (r) => {
      setMeldung(
        r.status === 'vorhanden'
          ? `${r.konto.email ?? email} ist jetzt für ${appName} freigeschaltet.`
          : `${r.konto.email ?? email}: ${EINLADUNG[r.einladung ?? ''] ?? 'angelegt'}.`,
      )
      setEmail('')
      setVorname('')
      setNachname('')
      neu()
    },
  })
  const entziehen = useMutation({
    mutationFn: (k: { sub: string; email: string | null }) =>
      trpc.umgebungCloud.entziehen.mutate({
        umgebungId,
        sub: k.sub,
        ...(k.email ? { email: k.email } : {}),
      }),
    onSuccess: () => {
      setEntziehenFrage(null)
      neu()
    },
  })

  return (
    <div className="grid gap-4">
      <p className="text-ink2">
        Wer {appName} im Umschalter sieht und sich anmelden darf – direkt freigeschaltet oder über
        eine Gruppe in NetCon.Cloud. Was jemand <i>in</i> der App darf, regelt die App selbst.
      </p>

      {darfAendern && (
        <div className="grid gap-2 rounded-xl border border-line p-3">
          <div className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr]">
            <input
              className={FELD}
              placeholder="E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              className={FELD}
              placeholder="Vorname (neu)"
              value={vorname}
              onChange={(e) => setVorname(e.target.value)}
            />
            <input
              className={FELD}
              placeholder="Nachname (neu)"
              value={nachname}
              onChange={(e) => setNachname(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={KNOPF_HAUPT}
              disabled={!email.includes('@') || einladen.isPending}
              onClick={() => {
                setMeldung(null)
                einladen.mutate()
              }}
            >
              {einladen.isPending ? 'wird freigeschaltet …' : 'Freischalten / einladen'}
            </button>
            <span className="text-[11.5px] text-muted">
              Gibt es das Konto schon, wird es nur freigeschaltet. Externe ohne NetCon bekommen eine
              Einladungsmail.
            </span>
          </div>
          {meldung && <p className="text-good-text">{meldung}</p>}
          {einladen.error && <p className="text-bad">{einladen.error.message}</p>}
        </div>
      )}

      {liste.error && <p className="text-bad">{liste.error.message}</p>}
      {liste.data && (
        <div className="overflow-hidden rounded-xl border border-line">
          <div className="bg-[#f7f7f5] px-3 py-1.5 text-[11.5px] text-muted">
            {liste.data.length === 0
              ? 'Noch niemand freigeschaltet.'
              : `${liste.data.length} freigeschaltet`}
          </div>
          {liste.data.map((k) => (
            <div
              key={k.sub}
              className="flex flex-wrap items-center gap-2 border-t border-line px-3 py-1.5"
            >
              <span className="min-w-0 flex-1">
                <b>{k.name ?? k.email}</b> {k.name && <span className="text-muted">{k.email}</span>}
                {k.extern && (
                  <span className="ml-1.5 rounded-full bg-[#f3f2ef] px-1.5 text-[10.5px] text-muted">
                    extern
                  </span>
                )}
                {!k.aktiv && (
                  <span className="ml-1.5 rounded-full bg-bad-bg px-1.5 text-[10.5px] text-bad">
                    gesperrt
                  </span>
                )}
              </span>
              {darfAendern &&
                (entziehenFrage === k.sub ? (
                  <span className="flex items-center gap-2 text-[11.5px]">
                    <span className="text-muted">
                      Nur die direkte Freischaltung – eine Gruppe bleibt.
                    </span>
                    <button
                      type="button"
                      className="cursor-pointer font-semibold text-bad hover:underline"
                      onClick={() => entziehen.mutate({ sub: k.sub, email: k.email })}
                    >
                      entziehen
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer text-muted hover:underline"
                      onClick={() => setEntziehenFrage(null)}
                    >
                      abbrechen
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="cursor-pointer text-[11.5px] text-muted hover:underline"
                    onClick={() => setEntziehenFrage(k.sub)}
                  >
                    Freischaltung entziehen
                  </button>
                ))}
            </div>
          ))}
        </div>
      )}
      {entziehen.error && <p className="text-bad">{entziehen.error.message}</p>}
    </div>
  )
}
